import React, { useRef } from 'react';
import { Text, Table, StatusChip } from '@fragments';
import { FilterListRounded, AccountTreeRounded } from '@mui/icons-material';

import { UserAvatar } from '@components/Community';
import Popper from '@components/Popper';
import Filter from '../../../components/Filter';
import MetricCard from '../../../components/MetricCard';
import Parser from '@helpers/parser';

import { CHARGEBACK_STATUSES, CHARGEBACK_STATUS_MAP } from '../enums/chargebackStatuses';

import './styles.scss';

function ChargebackStatusChip({ status }) {
    const config = CHARGEBACK_STATUS_MAP[status];
    if (!config) return <StatusChip.Default text={status} />;

    const components = {
        success: StatusChip.Success,
        error: StatusChip.Error,
        alert: StatusChip.Alert,
        info: StatusChip.Info,
        default: StatusChip.Default,
    };

    const Component = components[config.chipVariant] || StatusChip.Default;
    return <Component text={config.label} />;
}

const CATEGORY_LABELS = {
    fraudulent: 'Fraude',
    product_not_received: 'Produto não recebido',
    product_unacceptable: 'Produto inaceitável',
    subscription_canceled: 'Assinatura cancelada',
    unrecognized: 'Não reconhecida',
    duplicate: 'Duplicada',
    general: 'Geral',
};

export default function ChargebacksView({
    plans,
    selectedPlan,
    selectedStatuses,
    chargebacks,
    summary,
    loading,
    pagination,
    handlePlanChange,
    toggleStatus,
    clearStatuses,
    handlePageChange,
    navigate,
}) {
    const planPopperRef = useRef();
    const statusPopperRef = useRef();

    const planOptions = plans.map(p => ({ id: p.id, label: `${p.communityName} - ${p.label}` }));
    const hasPlans = planOptions.length > 0;

    function handlePlanFilterClick(event) {
        if (!hasPlans) return;
        const options = planOptions.map(option => ({
            ...option,
            onClick: () => {
                handlePlanChange(option.id);
                planPopperRef.current.close();
            },
        }));
        planPopperRef.current.open(options, 'bottom-start', event.currentTarget);
    }

    function handleStatusFilterClick(event) {
        // Re-build options with latest checked state each time
        const options = CHARGEBACK_STATUSES.map(s => ({
            id: s.id,
            label: s.label,
            checked: selectedStatuses.includes(s.id),
            onClick: () => toggleStatus(s.id),
        }));
        statusPopperRef.current.open(options, 'bottom-start', event.currentTarget);
    }

    const selectedPlanLabel = planOptions.find(p => p.id === selectedPlan)?.label || '—';
    const selectedStatusLabel = selectedStatuses.length === 0
        ? '—'
        : selectedStatuses.length === 1
            ? CHARGEBACK_STATUS_MAP[selectedStatuses[0]]?.label
            : `${selectedStatuses.length} status`;

    const columns = [
        {
            id: 'user',
            title: 'Cliente',
            grow: 1,
            customRender: (item) => (
                <div className="chargeback-user-cell">
                    <UserAvatar user={item.user} size={28} />
                    <Text.span contrast={7}>
                        {item.user?.username || '—'}
                    </Text.span>
                </div>
            ),
        },
        {
            id: 'plan',
            title: 'Plano',
            grow: 1,
            customRender: (item) => (
                <Text.span contrast={7} weight={500}>
                    {item.plan?.label || '—'}
                </Text.span>
            ),
        },
        {
            id: 'status',
            title: 'Status',
            align: 'center',
            minWidth: 160,
            customRender: (item) => <ChargebackStatusChip status={item.status} />,
        },
        {
            id: 'category',
            title: 'Alegação do cliente',
            align: 'center',
            minWidth: 140,
            customRender: (item) => (
                <Text.span contrast={5}>
                    {CATEGORY_LABELS[item.category] || item.category || '—'}
                </Text.span>
            ),
        },
        {
            id: 'amount',
            title: 'Valor da cobrança',
            minWidth: 120,
            render: (item) => `R$ ${Parser.price.brl(item.amount)}`,
        },
        {
            id: 'deadline',
            title: 'Prazo de contestação',
            minWidth: 160,
            customRender: (item) => {
                const deadline = item.timestamps?.deadline;
                if (!deadline) return <Text.span contrast={4}>—</Text.span>;

                const deadlineDate = new Date(deadline);
                const now = new Date();
                const diffDays = Math.ceil((deadlineDate - now) / (1000 * 60 * 60 * 24));
                const isUrgent = diffDays <= 3 && diffDays >= 0;
                const isPast = diffDays < 0;

                return (
                    <Text.span contrast={isPast ? 4 : isUrgent ? 8 : 6}>
                        {deadlineDate.toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                        })}
                    </Text.span>
                );
            },
        },
                {
            id: 'created',
            title: 'Solicitado em',
            minWidth: 160,
            customRender: (item) => {
                const created = item.timestamps?.created;
                if (!created) return <Text.span contrast={4}>—</Text.span>;

                const createdDate = new Date(created);
                const now = new Date();
                const diffDays = Math.ceil((createdDate - now) / (1000 * 60 * 60 * 24));
                const isUrgent = diffDays <= 3 && diffDays >= 0;
                const isPast = diffDays < 0;

                return (
                    <Text.span contrast={isPast ? 4 : isUrgent ? 8 : 6}>
                        {createdDate.toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                        })}
                    </Text.span>
                );
            },
        },
    ];

    return (
        <div className="chargebacks-tab">
            {/* Filters */}
            <Text.h4 className="mt-3 mb-1" left={<FilterListRounded />}>
                Filtros
            </Text.h4>

            <div className="filters-row">
                <Filter
                    name="Plano"
                    valueLabel={selectedPlanLabel}
                    onClick={handlePlanFilterClick}
                    icon={<AccountTreeRounded style={{ fontSize: '16px' }} />}
                    className={!hasPlans ? 'filter-item--disabled' : ''}
                    onClear={selectedPlan ? () => handlePlanChange(null) : undefined}
                />

                <Filter
                    name="Status"
                    valueLabel={selectedStatusLabel}
                    onClick={handleStatusFilterClick}
                    icon={<span style={{ fontSize: '10px', width: 16, height: 16, borderRadius: '50%', background: 'currentColor', display: 'inline-block', opacity: 0.6 }} />}
                    onClear={selectedStatuses.length > 0 ? clearStatuses : undefined}
                />
            </div>

            <Popper
                controlRef={planPopperRef}
                config={{
                    searchKeyExtractor: (opt, validate) => validate(opt.label),
                }}
            >
                <div style={{ display: 'none' }} />
            </Popper>
            <Popper
                controlRef={statusPopperRef}
                config={{ multiple: true }}
            >
                <div style={{ display: 'none' }} />
            </Popper>

            {/* Summary cards */}
            {summary && (
                <div className="metrics-row mt-3">
                    <MetricCard
                        title="Em andamento"
                        metric={summary.in_progress?.charges ?? 0}
                        submetric={`R$ ${Parser.price.brl(summary.in_progress?.amount ?? 0) || '0,00'}`}
                        loading={loading}
                    />
                    <MetricCard
                        title="Ganhos"
                        metric={summary.won?.charges ?? 0}
                        submetric={`R$ ${Parser.price.brl(summary.won?.amount ?? 0) || '0,00'}`}
                        loading={loading}
                    />
                    <MetricCard
                        title="Perdidos"
                        metric={summary.lost?.charges ?? 0}
                        submetric={`R$ ${Parser.price.brl(summary.lost?.amount ?? 0) || '0,00'}`}
                        loading={loading}
                    />
                </div>
            )}

            {/* Table */}
            <div className="chargebacks-table mt-3 mb-6">
                <Table
                    id="banking-chargebacks-table"
                    columns={columns}
                    data={chargebacks}
                    loading={loading}
                    loadingRows={5}
                    contained
                    emptyMessage="Nenhum chargeback encontrado"
                    enablePagination
                    pages={pagination.pages}
                    records={pagination.total}
                    page={pagination.current_page}
                    onSelectPage={handlePageChange}
                    onRowClick={(item) => navigate(`/banking/chargebacks/${item.id}`)}
                />
            </div>
        </div>
    );
}
